import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { CodeAIRequest, CodeAIResponse } from '../../../models/code-ai/code-ai.model';

/**
 * Service for CSS AI code generation
 */
@Injectable({
  providedIn: 'root'
})
export class CSSAIService {
  private get baseUrl(): string {
    // Use the same API endpoint as other services
    return environment.api.API_ENDPOINT;
  }

  constructor(private http: HttpClient) {}

  /**
   * Generate CSS code based on natural language prompt and preference page ID
   * @param request Code AI request with preference page ID and prompt
   * @returns Observable of generated CSS response
   */
  generateCSS(request: Omit<CodeAIRequest, 'type' | 'html'> & { preferencePageId: string }): Observable<CodeAIResponse> {
    const fullRequest: CodeAIRequest = {
      ...request,
      type: 'css',
      html: '' // No longer needed - backend generates from preferencePageId
    };

    // Add 'skip' header to bypass interceptor notifications
    const headers = { 'skip': 'true' };

    return this.http.post<CodeAIResponse>(
      `${this.baseUrl}/ai/generate-css`,
      fullRequest,
      { headers }
    ).pipe(
      map((response: any) => {
        // Handle response - don't force success property
        console.log('CSS AI Response:', response);
        
        // If response is successful, ensure it has the expected structure
        if (response && (response.success || response.code)) {
          return {
            code: response.code || '',
            explanation: response.explanation || '',
            validation: response.validation || { isValid: true, warnings: [], securityIssues: [], syntaxErrors: [] },
            success: response.success !== false,
            error: response.error || null
          } as CodeAIResponse;
        }
        
        // If response indicates failure
        return {
          code: '',
          explanation: '',
          validation: { isValid: false, warnings: [], securityIssues: [], syntaxErrors: [] },
          success: false,
          error: response.error || 'Failed to generate CSS'
        } as CodeAIResponse;
      }),
      catchError((error: any) => {
        console.error('CSS AI Generation Error:', error);
        
        // Return a proper error response instead of throwing
        return of({
          code: '',
          explanation: '',
          validation: { isValid: false, warnings: [], securityIssues: [], syntaxErrors: [] },
          success: false,
          error: error?.error?.message || error?.message || 'Failed to generate CSS'
        } as CodeAIResponse);
      })
    );
  }

  /**
   * Validate CSS code
   * @param css CSS code to validate
   * @returns Observable of validation result
   */
  validateCSS(css: string): Observable<{ isValid: boolean; errors?: string[] }> {
    return this.http.post<{ isValid: boolean; errors?: string[] }>(
      `${this.baseUrl}/ai/validate-css`,
      { css }
    ).pipe(
      catchError((error: any) => {
        console.error('CSS Validation Error:', error);
        return of({
          isValid: false,
          errors: [error.message || 'Validation failed']
        });
      })
    );
  }

  /**
   * Get CSS suggestions based on preference page
   * @param preferencePageId ID of the preference page
   * @returns Observable of CSS suggestions
   */
  getSuggestions(preferencePageId: string): Observable<string[]> {
    return this.http.post<{ suggestions: string[] }>(
      `${this.baseUrl}/ai/css-suggestions`,
      { preferencePageId }
    ).pipe(
      map(response => response.suggestions || []),
      catchError((error: any) => {
        console.error('CSS Suggestions Error:', error);
        return of([]);
      })
    );
  }
}
